import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Pagination from '../../components/ui/Pagination';
import ConfirmModal from '../../components/ui/ConfirmModal';

/**
 * Staff → Accounts (admin és owner) — az app-belépések (2026-10-08). A tulajdonos: „az app fiókokat hol tudom
 * megnézni? … nem kéne az app oldalra is egy admin rész?” + „a hwid reset csak app oldalon lehet majd”.
 * Lista keresővel (app-név vagy Codex-név), soronként: a belépés visszavonása (új kód kell — /invite) és a HWID-reset.
 * A meghívó-kódok külön fülön (InvitesTab.jsx). A szerver: bot-backend controllers/appAdmin.controller.js.
 */

const ACCESS = {
  lifetime: { label: 'Lifetime', cls: 'bg-brand-500/12 text-brand-200 border-brand-400/25' },
  live:     { label: 'Active',   cls: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25' },
  paused:   { label: 'Paused',   cls: 'bg-sky-500/12 text-sky-300 border-sky-500/25' },
  ended:    { label: 'Expired',  cls: 'bg-red-500/12 text-red-300 border-red-500/25' },
  none:     { label: 'No access', cls: 'bg-white/[0.05] text-gray-400 border-white/[0.08]' },
};

// Az időpontok UTC-ben jönnek; a staff helyi idejében mutatjuk.
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

function Chip({ map, value }) {
  const c = map[value] || map.none || { label: value, cls: '' };
  return <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-medium ${c.cls}`}>{c.label}</span>;
}

export default function AccountsTab() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);   // { kind: 'revoke' | 'hwid', account }
  const [busy, setBusy] = useState(false);

  // A keresőmező kis késleltetéssel kerül a lekérdezésbe.
  useEffect(() => {
    const t = setTimeout(() => { setTerm(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/app-accounts', { params: { search: term, page } });
      setData(res.data);
    } catch (err) {
      setData(null);
      toast.error(err.response?.data?.error || 'The accounts could not be loaded.');
    } finally {
      setLoading(false);
    }
    // A toast nem függőség: ha a kontextus új objektumot ad, a lista ne töltődjön újra körbe-körbe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, page]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    const { kind, account } = confirm;
    setBusy(true);
    try {
      const res = await api.post(`/admin/app-accounts/${account.id}/${kind === 'revoke' ? 'revoke' : 'reset-hwid'}`);
      toast.success(res.data.message);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const accounts = data?.accounts ?? [];

  return (
    <div className="space-y-6">
      <PanelCard
        title="App accounts"
        subtitle="App logins created with an invite code. Search by app username or MortisCodex username."
        flush
      >
        <div className="px-4 sm:px-5 py-3 border-b border-line-faint">
          <input type="text" className="input" placeholder="Search accounts…" value={search}
                 onChange={e => setSearch(e.target.value)} />
        </div>

        {loading && !data ? (
          <div className="py-10 flex justify-center"><LoadingSpinner /></div>
        ) : accounts.length === 0 ? (
          <EmptyState title={term ? 'No account matches this search.' : 'No app logins yet.'}
                      hint={term ? null : 'Accounts appear here once a member registers with an /invite code.'} />
        ) : (
          <ul className={`divide-y divide-line-faint ${loading ? 'opacity-60' : ''}`}>
            {accounts.map(a => (
              <li key={a.id} className="px-4 sm:px-5 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-[200px] flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="text-[15px] font-semibold text-gray-50">{a.appUsername}</b>
                    <span className="font-mono text-[11px] text-gray-500">{a.role}</span>
                    {a.banned && <span className="inline-flex px-2 py-0.5 rounded-md border text-[11px] font-medium bg-red-500/12 text-red-300 border-red-500/25">Banned</span>}
                  </div>
                  <div className="mt-0.5 text-[12.5px] text-gray-500">
                    MortisCodex: <span className="text-gray-300">{a.codexUsername}</span>
                    {' · '}registered {when(a.registeredAt)}
                    {' · '}last sign-in {when(a.lastLoginAt)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Chip map={ACCESS} value={a.access} />
                  <span className="text-[12px] text-gray-500">{a.hwidBound ? 'HWID bound' : 'No HWID'}</span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <button type="button" className="btn-glass btn-sm" disabled={!a.hwidBound}
                          onClick={() => setConfirm({ kind: 'hwid', account: a })}>
                    Reset HWID
                  </button>
                  <button type="button" className="btn-glass btn-sm text-red-300"
                          onClick={() => setConfirm({ kind: 'revoke', account: a })}>
                    Revoke login
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {data && (
          <div className="px-4 sm:px-5 py-3">
            <Pagination page={data.page} totalPages={data.pages} total={data.total} label="accounts" onPage={setPage} />
          </div>
        )}
      </PanelCard>

      {confirm && (
        <ConfirmModal
          danger
          title={confirm.kind === 'revoke' ? 'Revoke app login?' : 'Reset HWID?'}
          message={confirm.kind === 'revoke'
            ? `${confirm.account.appUsername} will be signed out of the app and the loader, and needs a new /invite code to sign in again. Their MortisCodex account is not affected.`
            : `The PC binding of ${confirm.account.appUsername} is removed. The next loader sign-in binds their current PC.`}
          confirmLabel={busy ? 'Working…' : (confirm.kind === 'revoke' ? 'Revoke login' : 'Reset HWID')}
          onConfirm={busy ? () => {} : run}
          onCancel={() => { if (!busy) setConfirm(null); }}
        />
      )}
    </div>
  );
}
